// pages/quotations/QuotationList.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { userActions } from '../../core/action/userAction';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import dayjs from 'dayjs';
import Select from 'react-select';
import { DatePicker } from 'antd';
import Table from '../../core/pagination/datatable';
import {
    ChevronUp,
    RotateCcw,
    Sliders,
    StopCircle,
    User,
    Calendar,
    Download,
    Printer,
    Filter,
    Search,
    Eye,
    Edit,
    Trash2,
    PlusCircle,
    FileText,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Clock,
    RefreshCw,
    DollarSign,
    File
} from 'feather-icons-react/build/IconComponents';
import { quotationActions } from '../../core/action/quotationActions';
import { customerActions } from '../../core/action/customerActions';
import { companyActions } from '../../core/action/companyActions';
import ViewQuotation from '../../core/modals/quotations/ViewQuotation';
import EditQuotation from '../../core/modals/quotations/EditQuotation';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const { RangePicker } = DatePicker;
const MySwal = withReactContent(Swal);

const QuotationList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // Permission checks
    const canCreate = userActions.hasPermission('create-quotations');
    const canEdit = userActions.hasPermission('edit-quotations');
    const canDelete = userActions.hasPermission('delete-quotations');
    //const canView = userActions.hasPermission('view-quotations');

    // State
    const [quotations, setQuotations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [companySettings, setCompanySettings] = useState({});

    // Dropdown options
    const [customers, setCustomers] = useState([]);

    // Modal states
    const [modalType, setModalType] = useState(null); // 'view', 'edit'
    const [selectedQuotation, setSelectedQuotation] = useState(null);

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        startDate: null,
        endDate: null,
        customer_id: 0,
        status: ''
    });

    // Sort options
    const sortOptions = [
        { value: 'newest', label: 'Newest First' },
        { value: 'oldest', label: 'Oldest First' },
        { value: 'quotation_asc', label: 'Quotation No (A-Z)' },
        { value: 'quotation_desc', label: 'Quotation No (Z-A)' },
        { value: 'amount_high', label: 'Highest Amount' },
        { value: 'amount_low', label: 'Lowest Amount' },
        { value: 'customer_asc', label: 'Customer Name (A-Z)' },
        { value: 'customer_desc', label: 'Customer Name (Z-A)' }
    ];

    const [sortOption, setSortOption] = useState('newest');

    // Status filter options
    const statusFilterOptions = [
        { value: '', label: 'All Status' },
        { value: 'draft', label: 'Draft' },
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'expired', label: 'Expired' },
        { value: 'converted', label: 'Converted' }
    ];

    // Date range presets
    const datePresets = {
        'Today': [dayjs(), dayjs()],
        'Yesterday': [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')],
        'Last 7 Days': [dayjs().subtract(7, 'day'), dayjs()],
        'Last 30 Days': [dayjs().subtract(30, 'day'), dayjs()],
        'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
        'Last Month': [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')]
    };

    // Load initial data
    useEffect(() => {
        loadCompanySettings();
        loadCustomers();
        loadQuotations();
    }, []);

    // Load company settings
    const loadCompanySettings = async () => {
        try {
            const result = await companyActions.getSettings();
            if (result.success) {
                setCompanySettings(result.data || {});
            }
        } catch (error) {
            console.error('Error loading company settings:', error);
        }
    };

    // Load customers
    const loadCustomers = async () => {
        try {
            const result = await customerActions.getCustomers({ limit: 1000 });
            if (result.success) {
                setCustomers([
                    { value: 0, label: 'All Customers' },
                    ...result.data.map(c => ({
                        value: c.id,
                        label: `${c.name} (${c.phone || 'No phone'})`
                    }))
                ]);
            }
        } catch (error) {
            console.error('Error loading customers:', error);
        }
    };

    // Load quotations
    const loadQuotations = async (page = pagination.page) => {
        try {
            setLoading(true);

            const params = {
                page,
                limit: pagination.limit,
                search: filters.search,
                start_date: filters.startDate ? filters.startDate.format('YYYY-MM-DD') : undefined,
                end_date: filters.endDate ? filters.endDate.format('YYYY-MM-DD') : undefined,
                customer_id: filters.customer_id || undefined,
                status: filters.status || undefined,
                sort: sortOption
            };

            const result = await quotationActions.getQuotations(params);

            if (result.success) {
                setQuotations(result.data || []);
                setPagination({
                    page: result.page || page,
                    limit: pagination.limit,
                    total: result.total || 0,
                    totalPages: result.totalPages || 1
                });
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.message || 'Failed to load quotations',
                    timer: 2000
                });
            }
        } catch (error) {
            console.error('Error loading quotations:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load quotations',
                timer: 2000
            });
        } finally {
            setLoading(false);
        }
    };

    // Handle search
    const handleSearch = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        loadQuotations(1);
    };

    // Toggle filter visibility
    const toggleFilterVisibility = () => {
        setIsFilterVisible((prev) => !prev);
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            search: '',
            startDate: null,
            endDate: null,
            customer_id: 0,
            status: ''
        });
        setSortOption('newest');
        setPagination(prev => ({ ...prev, page: 1 }));
        loadQuotations(1);
    };

    // Handle date range change
    const handleDateRangeChange = (dates) => {
        if (dates && dates.length === 2) {
            setFilters(prev => ({
                ...prev,
                startDate: dates[0],
                endDate: dates[1]
            }));
        } else {
            setFilters(prev => ({
                ...prev,
                startDate: null,
                endDate: null
            }));
        }
    };

    // Handle filter change
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({ ...prev, [filterName]: value }));
    };

    // Handle sort change
    const handleSortChange = (option) => {
        setSortOption(option?.value || 'newest');
        setPagination(prev => ({ ...prev, page: 1 }));
        setTimeout(() => loadQuotations(1), 0);
    };

    // Handle page change
    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, page }));
        loadQuotations(page);
    };

    // Modal handlers
    const handleView = (quotation) => {
        setSelectedQuotation(quotation);
        setModalType('view');
    };

    const handleEdit = (quotation) => {
        if (!canEdit) {
            MySwal.fire({
                title: 'Access Denied',
                text: 'You do not have permission to edit quotations',
                icon: 'error'
            });
            return;
        }
        setSelectedQuotation(quotation);
        setModalType('edit');
    };

    const handleModalClose = () => {
        setModalType(null);
        setSelectedQuotation(null);
    };

    const handleSuccess = () => {
        loadQuotations(pagination.page);
        handleModalClose();
    };

    // Handle delete
    const handleDelete = async (quotation) => {
        if (!canDelete) {
            MySwal.fire({
                title: 'Access Denied',
                text: 'You do not have permission to delete quotations',
                icon: 'error'
            });
            return;
        }
        const result = await MySwal.fire({
            title: 'Delete Quotation?',
            html: `
                <div class="text-start">
                    <p>Are you sure you want to delete this quotation?</p>
                    <p><strong>Quotation No:</strong> ${quotation.quotation_no}</p>
                    <p><strong>Customer:</strong> ${quotation.customer_name}</p>
                    <p><strong>Amount:</strong> ₹${parseFloat(quotation.grand_total || 0).toFixed(2)}</p>
                    <p class="text-danger">This action cannot be undone.</p>
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const deleteResult = await quotationActions.deleteQuotation(quotation.id);

                if (deleteResult.success) {
                    MySwal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Quotation has been deleted.',
                        timer: 2000
                    });
                    loadQuotations(pagination.page);
                } else {
                    MySwal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: deleteResult.message || 'Failed to delete quotation',
                        timer: 2000
                    });
                }
            } catch (error) {
                console.error('Error deleting quotation:', error);
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to delete quotation',
                    timer: 2000
                });
            } finally {
                setLoading(false);
            }
        }
    };

    // Handle convert to sale
    const handleConvertToSale = async (id) => {
        const result = await MySwal.fire({
            title: 'Convert to Sale?',
            text: 'This will create a sales invoice from this quotation. Continue?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, convert'
        });

        if (result.isConfirmed) {
            try {
                const convertResult = await quotationActions.convertToSale(id);
                if (convertResult.success) {
                    MySwal.fire({
                        icon: 'success',
                        title: 'Converted!',
                        text: 'Quotation converted to sale successfully',
                        timer: 2000
                    });
                    loadQuotations(pagination.page);
                } else {
                    MySwal.fire('Error', convertResult.message || 'Failed to convert', 'error');
                }
            } catch (error) {
                console.error('Error converting quotation:', error);
                MySwal.fire('Error', 'Failed to convert quotation', 'error');
            }
        }
    };

    // pages/quotations/QuotationList.jsx - PREMIUM PDF Design

    /**
     * Premium PDF Generation Function - Clean Design + Correct Calculations
     */
    const downloadQuotationPDF = async (quotation) => {
        try {
            setLoading(true);

            MySwal.fire({
                title: 'Generating PDF...',
                text: 'Please wait',
                allowOutsideClick: false,
                didOpen: () => { MySwal.showLoading(); }
            });

            const result = await quotationActions.getQuotation(quotation.id);

            if (!result.success || !result.data) {
                MySwal.fire({ icon: 'error', title: 'Error', text: result?.message || 'Failed to load quotation details', timer: 2000 });
                return;
            }

            const fullQuotation = result.data;

            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageWidth = doc.internal.pageSize.width;
            const pageHeight = doc.internal.pageSize.height;
            const margin = 15;
            let yPos = 0;

            // ── COLOR PALETTE ──────────────────────────────────────────
            const BRAND = [30, 80, 160];   // deep blue
            const BRAND_LIGHT = [235, 241, 255];  // light blue tint
            const ACCENT = [255, 140, 0];   // orange accent
            const DARK = [30, 30, 40];
            const MUTED = [110, 110, 125];
            const LIGHT_GRAY = [245, 245, 248];
            const BORDER = [210, 215, 225];
            const WHITE = [255, 255, 255];

            // ── HELPER: currency (no unicode ₹ — jsPDF Helvetica can't render it)
            const fmt = (n) => `Rs. ${parseFloat(n || 0).toFixed(2)}`;

            // ── HEADER BANNER ──────────────────────────────────────────
            // Full-width dark blue banner
            doc.setFillColor(...BRAND);
            doc.rect(0, 0, pageWidth, 28, 'F');



            // Company Logo
            if (companySettings?.company_logo) {
                try {
                    let logoUrl = companySettings.company_logo;
                    if (!logoUrl.startsWith('http')) logoUrl = `${window.location.origin}/${logoUrl.replace(/^\/+/, '')}`;
                    doc.addImage(logoUrl, 'PNG', margin, 5, 30, 10, undefined, 'FAST');
                } catch (e) { /* skip */ }
            }

            // Company name in banner
            doc.setFontSize(15);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...WHITE);
            const compName = companySettings?.company_name || 'Your Company Name';
            doc.text(compName, margin + 33, 12);

            // GSTIN in banner
            if (companySettings?.gst_number) {
                doc.setFontSize(7.5);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(200, 215, 255);
                doc.text(`GSTIN: ${companySettings.gst_number}`, margin + 33, 18);
            }

            // Address & contact in banner (right-aligned)
            const rightX = pageWidth - margin;
            let bannerRy = 9;
            doc.setFontSize(7.5);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(210, 220, 255);
            if (companySettings?.company_phone) { doc.text(`Ph: ${companySettings.company_phone}`, rightX, bannerRy, { align: 'right' }); bannerRy += 4.5; }
            if (companySettings?.company_email) { doc.text(`Email: ${companySettings.company_email}`, rightX, bannerRy, { align: 'right' }); bannerRy += 4.5; }
            if (companySettings?.company_address) {
                const adr = doc.splitTextToSize(companySettings.company_address, 70);
                doc.text(adr[0], rightX, bannerRy, { align: 'right' });
            }

            // Orange accent bar below banner
            doc.setFillColor(...ACCENT);
            doc.rect(0, 28, pageWidth, 2, 'F');

            yPos = 38;

            // ── DOC ID ROW (Quotation title + meta) ───────────────────
            // Left: QUOTATION label
            doc.setFontSize(20);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...BRAND);
            doc.text('QUOTATION', margin, yPos);

            // Right: Quotation # and Date in small pill boxes
            const quotationNo = fullQuotation.quotation_no || quotation.quotation_no || 'N/A';
            const quotationDate = fullQuotation.quotation_date || quotation.quotation_date || null;
            const expiryDate = fullQuotation.expiry_date || quotation.expiry_date;

            const metaRightX = pageWidth - margin;
            let metaY = yPos - 5;

            // Pill: Quotation No
            doc.setFillColor(...BRAND_LIGHT);
            doc.roundedRect(metaRightX - 75, metaY, 75, 7, 1.5, 1.5, 'F');
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...BRAND);
            doc.text('No:', metaRightX - 72, metaY + 4.8);
            doc.setFont('helvetica', 'normal');
            doc.text(quotationNo, metaRightX - 2, metaY + 4.8, { align: 'right' });
            metaY += 9;

            // Pill: Date
            doc.setFillColor(...LIGHT_GRAY);
            doc.roundedRect(metaRightX - 75, metaY, 75, 7, 1.5, 1.5, 'F');
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...MUTED);
            doc.text('Date:', metaRightX - 72, metaY + 4.8);
            doc.setFont('helvetica', 'normal');
            doc.text(quotationDate ? dayjs(quotationDate).format('DD MMM YYYY') : 'N/A', metaRightX - 2, metaY + 4.8, { align: 'right' });
            metaY += 9;

            // Pill: Valid Until
            if (expiryDate) {
                doc.setFillColor(...LIGHT_GRAY);
                doc.roundedRect(metaRightX - 75, metaY, 75, 7, 1.5, 1.5, 'F');
                doc.setFontSize(8);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(...MUTED);
                doc.text('Valid Until:', metaRightX - 72, metaY + 4.8);
                doc.setFont('helvetica', 'normal');
                doc.text(dayjs(expiryDate).format('DD MMM YYYY'), metaRightX - 2, metaY + 4.8, { align: 'right' });
            }

            yPos += 10;

            // Thin full-width rule
            doc.setDrawColor(...BORDER);
            doc.setLineWidth(0.3);
            doc.line(margin, yPos, pageWidth - margin, yPos);
            yPos += 6;

            // ── BILL TO SECTION ────────────────────────────────────────
            // Section header bar
            doc.setFillColor(...BRAND);
            doc.roundedRect(margin, yPos, (pageWidth - margin * 2) * 0.55, 6.5, 1, 1, 'F');
            doc.setFontSize(8.5);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...WHITE);
            doc.text('BILL TO', margin + 3, yPos + 4.5);
            yPos += 9;

            // Customer info block (left column, 55% width)
            const custBlockW = (pageWidth - margin * 2) * 0.55;
            doc.setFillColor(...LIGHT_GRAY);
            const custBlockStartY = yPos;

            const customerName = fullQuotation.customer_name || quotation.customer_name || 'N/A';
            const customerPhone = fullQuotation.customer_phone || quotation.customer_phone;
            const customerEmail = fullQuotation.customer_email || quotation.customer_email;
            const customerGst = fullQuotation.customer_gst || quotation.customer_gst;
            const customerAddress = fullQuotation.customer_address || quotation.customer_address;

            let custY = yPos + 3;

            // Customer name (bold, larger)
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...DARK);
            doc.text(customerName, margin + 2, custY);
            custY += 5.5;

            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...MUTED);

            if (customerPhone) { doc.text(`Ph: ${customerPhone}`, margin + 2, custY); custY += 4.5; }
            if (customerEmail) { doc.text(`Email: ${customerEmail}`, margin + 2, custY); custY += 4.5; }
            if (customerGst) { doc.text(`GSTIN: ${customerGst}`, margin + 2, custY); custY += 4.5; }
            if (customerAddress) {
                const addrLines = doc.splitTextToSize(`Addr: ${customerAddress}`, custBlockW - 5);
                doc.text(addrLines, margin + 2, custY);
                custY += addrLines.length * 4.5;
            }

            // Background for customer block
            doc.setFillColor(...LIGHT_GRAY);
            doc.roundedRect(margin, custBlockStartY, custBlockW, custY - custBlockStartY + 3, 1.5, 1.5, 'F');

            // Re-draw text on top of background
            custY = custBlockStartY + 3;
            doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(...DARK);
            doc.text(customerName, margin + 3, custY); custY += 5.5;
            doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(...MUTED);
            if (customerPhone) { doc.text(`Ph: ${customerPhone}`, margin + 3, custY); custY += 4.5; }
            if (customerEmail) { doc.text(`Email: ${customerEmail}`, margin + 3, custY); custY += 4.5; }
            if (customerGst) { doc.text(`GSTIN: ${customerGst}`, margin + 3, custY); custY += 4.5; }
            if (customerAddress) {
                const al = doc.splitTextToSize(`Addr: ${customerAddress}`, custBlockW - 6);
                doc.text(al, margin + 3, custY);
                custY += al.length * 4.5;
            }

            yPos = custY + 8;

            // ── ITEMS TABLE ────────────────────────────────────────────
            const items = fullQuotation.items || quotation.items || [];
            const subtotal = parseFloat(fullQuotation.subtotal || quotation.subtotal || 0);
            const tax = parseFloat(fullQuotation.tax || quotation.tax || 0);
            const discount = parseFloat(fullQuotation.discount || quotation.discount || 0);
            const shipping = parseFloat(fullQuotation.shipping || quotation.shipping || 0);
            const adjustment = parseFloat(fullQuotation.adjustment || quotation.adjustment || 0);
            const roundOff = parseFloat(fullQuotation.round_off || quotation.round_off || 0);
            const grandTotal = parseFloat(fullQuotation.grand_total || quotation.grand_total || 0);

            const tableBody = [];
            if (items && items.length > 0) {
                items.forEach((item, index) => {
                    const productName = item.product_name || item.productName || 'Product';
                    const hsnCode = item.hsn_code || item.hsnCode || '-';
                    const quantity = parseFloat(item.quantity) || 1;
                    const unitPrice = parseFloat(item.unit_price || item.unitPrice || item.price || 0);
                    const discountPct = parseFloat(item.discount) || 0;
                    const taxPct = parseFloat(item.tax_percent || item.taxPercent || 0);
                    const totalAmount = parseFloat(item.total_cost || item.totalCost || item.amount || 0);

                    tableBody.push([
                        (index + 1).toString(),
                        productName,
                        hsnCode,
                        quantity.toString(),
                        unitPrice.toFixed(2),
                        `${discountPct}%`,
                        `${taxPct}%`,
                        totalAmount.toFixed(2)
                    ]);
                });
            } else {
                tableBody.push(['1', 'No items found', '-', '0', '0.00', '0%', '0%', '0.00']);
            }

            autoTable(doc, {
                startY: yPos,
                head: [['#', 'Product Description', 'HSN', 'Qty', 'Unit Price', 'Disc%', 'Tax%', 'Amount']],
                body: tableBody,
                theme: 'plain',
                headStyles: {
                    fillColor: BRAND,
                    textColor: WHITE,
                    fontStyle: 'bold',
                    fontSize: 8.5,
                    halign: 'center',
                    cellPadding: { top: 3, bottom: 3, left: 2, right: 2 },
                    lineColor: BRAND,
                    lineWidth: 0
                },
                bodyStyles: {
                    fontSize: 8,
                    textColor: DARK,
                    cellPadding: { top: 2.5, bottom: 2.5, left: 2, right: 2 },
                    lineColor: BORDER,
                    lineWidth: 0.2
                },
                alternateRowStyles: { fillColor: [248, 249, 255] },
                rowPageBreak: 'auto',
                columnStyles: {
                    0: { cellWidth: 8, halign: 'center' },
                    1: { cellWidth: 58, halign: 'left' },
                    2: { cellWidth: 16, halign: 'center' },
                    3: { cellWidth: 14, halign: 'center' },
                    4: { cellWidth: 22, halign: 'right' },
                    5: { cellWidth: 12, halign: 'center' },
                    6: { cellWidth: 12, halign: 'center' },
                    7: { cellWidth: 24, halign: 'right' }
                },
                margin: { left: margin, right: margin },
                tableWidth: 'auto',
                didDrawPage: () => {
                    // Re-draw orange accent on new pages
                    doc.setFillColor(...ACCENT);
                    doc.rect(0, 28, pageWidth, 2, 'F');
                }
            });

            // ── SUMMARY SECTION ────────────────────────────────────────
            const tableEndY = doc.lastAutoTable.finalY;
            const summaryW = 80;
            const summaryX = pageWidth - margin - summaryW;
            let summaryY = tableEndY + 6;

            // Summary background card
            const cgst = tax / 2;
            const sgst = tax / 2;

            const summaryRows = [
                { label: 'Subtotal', value: fmt(subtotal), bold: false },
                { label: 'CGST', value: fmt(cgst), bold: false },
                { label: 'SGST', value: fmt(sgst), bold: false },
                { label: 'Additional Discount', value: `- ${fmt(discount)}`, bold: false },
                { label: 'Shipping', value: fmt(shipping), bold: false },
                { label: 'Adjustment', value: fmt(adjustment), bold: false },
                { label: 'Round Off', value: fmt(roundOff), bold: false },
            ];

            const rowH = 7;
            const totalCardH = summaryRows.length * rowH + 13; // extra for grand total

            // Card shadow (faked with slightly darker rect offset)
            doc.setFillColor(220, 225, 240);
            doc.roundedRect(summaryX + 1, summaryY + 1, summaryW, totalCardH, 2, 2, 'F');

            // Card background
            doc.setFillColor(...WHITE);
            doc.roundedRect(summaryX, summaryY, summaryW, totalCardH, 2, 2, 'F');
            doc.setDrawColor(...BORDER);
            doc.setLineWidth(0.3);
            doc.roundedRect(summaryX, summaryY, summaryW, totalCardH, 2, 2, 'S');

            // Card header
            doc.setFillColor(...BRAND_LIGHT);
            doc.roundedRect(summaryX, summaryY, summaryW, 7, 2, 2, 'F');
            doc.rect(summaryX, summaryY + 3.5, summaryW, 3.5, 'F'); // flatten bottom corners
            doc.setFontSize(8.5);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...BRAND);
            doc.text('Summary', summaryX + summaryW / 2, summaryY + 5, { align: 'center' });
            summaryY += 9;

            // Rows
            summaryRows.forEach((row, i) => {
                if (i % 2 === 0) {
                    doc.setFillColor(...LIGHT_GRAY);
                    doc.rect(summaryX, summaryY - 1.5, summaryW, rowH, 'F');
                }
                doc.setFontSize(8);
                doc.setFont('helvetica', row.bold ? 'bold' : 'normal');
                doc.setTextColor(...MUTED);
                doc.text(row.label + ':', summaryX + 3, summaryY + 3.5);
                doc.setTextColor(...DARK);
                doc.text(row.value, summaryX + summaryW - 3, summaryY + 3.5, { align: 'right' });
                summaryY += rowH;
            });

            // Divider
            doc.setDrawColor(...BORDER);
            doc.setLineWidth(0.4);
            doc.line(summaryX + 3, summaryY, summaryX + summaryW - 3, summaryY);
            summaryY += 3;

            // Grand Total row — highlighted
            doc.setFillColor(...BRAND);
            doc.roundedRect(summaryX, summaryY - 1, summaryW, 11, 0, 0, 'F');
            doc.rect(summaryX, summaryY - 1, summaryW, 6, 'F'); // flatten top corners
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...WHITE);
            doc.text('Grand Total', summaryX + 3, summaryY + 6);
            doc.text(fmt(grandTotal), summaryX + summaryW - 3, summaryY + 6, { align: 'right' });

            // ── NOTES & TERMS ──────────────────────────────────────────
            let notesY = tableEndY + 8;
            const notesMaxW = summaryX - margin - 5;

            const notes = fullQuotation.notes || quotation.notes;
            if (notes) {
                doc.setFontSize(9);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(...DARK);
                doc.text('Notes:', margin, notesY);
                notesY += 5;
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(8);
                doc.setTextColor(...MUTED);
                const nl = doc.splitTextToSize(notes, notesMaxW);
                doc.text(nl, margin, notesY);
                notesY += nl.length * 4.5 + 4;
            }

            const terms = fullQuotation.terms_conditions || quotation.terms_conditions;
            if (terms) {
                doc.setFontSize(9);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(...DARK);
                doc.text('Terms & Conditions:', margin, notesY);
                notesY += 5;
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(7.5);
                doc.setTextColor(...MUTED);
                const tl = doc.splitTextToSize(terms, notesMaxW);
                doc.text(tl, margin, notesY);
            }

            // ── FOOTER ────────────────────────────────────────────────
            const totalPages = doc.internal.getNumberOfPages();
            for (let p = 1; p <= totalPages; p++) {
                doc.setPage(p);

                // Footer bar
                doc.setFillColor(...BRAND);
                doc.rect(0, pageHeight - 12, pageWidth, 12, 'F');

                doc.setFontSize(7);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(200, 215, 255);
                doc.text('This is a computer-generated quotation — valid with digital signature.', margin, pageHeight - 5);
                doc.text(
                    `Page ${p} of ${totalPages}  |  Generated: ${dayjs().format('DD MMM YYYY, HH:mm')}`,
                    pageWidth - margin,
                    pageHeight - 5,
                    { align: 'right' }
                );

                // Accent line above footer
                doc.setFillColor(...ACCENT);
                doc.rect(0, pageHeight - 13, pageWidth, 1, 'F');
            }

            // Close loading Swal
            MySwal.close();

            // Save PDF
            const fileName = `Quotation_${quotationNo.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
            doc.save(fileName);

            // Success message
            MySwal.fire({
                icon: 'success',
                title: 'Success',
                text: 'PDF downloaded successfully',
                timer: 1500,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error generating PDF:', error);
            MySwal.close();
            MySwal.fire({
                icon: 'error',
                title: 'PDF Generation Failed',
                text: error.message || 'Unknown error occurred',
                confirmButtonText: 'OK'
            });
        } finally {
            setLoading(false);
        }
    };
    // Handle select all rows
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedRows(quotations.map(q => q.id));
        } else {
            setSelectedRows([]);
        }
    };

    // Handle select row
    const handleSelectRow = (id, checked) => {
        if (checked) {
            setSelectedRows([...selectedRows, id]);
        } else {
            setSelectedRows(selectedRows.filter(rowId => rowId !== id));
        }
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const badges = {
            'draft': { class: 'badge bg-secondary', icon: <FileText size={12} /> },
            'pending': { class: 'badge bg-warning', icon: <Clock size={12} /> },
            'approved': { class: 'badge bg-success', icon: <CheckCircle size={12} /> },
            'rejected': { class: 'badge bg-danger', icon: <XCircle size={12} /> },
            'expired': { class: 'badge bg-dark', icon: <AlertTriangle size={12} /> },
            'converted': { class: 'badge bg-info', icon: <RefreshCw size={12} /> }
        };
        const badge = badges[status] || { class: 'badge bg-secondary', icon: null };
        return (
            <span className={badge.class}>
                {badge.icon} {status}
            </span>
        );
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    // Format date
    const formatDate = (date) => {
        return date ? dayjs(date).format('DD/MM/YYYY') : 'N/A';
    };

    // Check if quotation is expired
    const isExpired = (expiryDate) => {
        return expiryDate && dayjs(expiryDate).isBefore(dayjs());
    };

    // Calculate statistics
    const getSummaryStats = () => {
        const stats = {
            totalQuotations: pagination.total,
            totalAmount: 0,
            pendingCount: 0,
            approvedCount: 0,
            convertedCount: 0
        };

        quotations.forEach(q => {
            stats.totalAmount += q.grand_total || 0;
            if (q.status === 'pending') stats.pendingCount++;
            if (q.status === 'approved') stats.approvedCount++;
            if (q.status === 'converted') stats.convertedCount++;
        });

        return stats;
    };

    const stats = getSummaryStats();

    // Tooltip renderer
    const renderTooltip = (props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    );

    // Table columns with PDF download button
    const columns = [
        {
            title: <input
                type="checkbox"
                className="form-check-input"
                checked={selectedRows.length === quotations.length && quotations.length > 0}
                onChange={handleSelectAll}
            />,
            dataIndex: "checkbox",
            key: "checkbox",
            width: "40px",
            render: (text, record) => (
                <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedRows.includes(record.id)}
                    onChange={(e) => handleSelectRow(record.id, e.target.checked)}
                />
            ),
        },
        {
            title: "Quotation No",
            dataIndex: "quotation_no",
            sorter: true,
            render: (text, record) => (
                <div>
                    <strong>{text}</strong>
                    <br />
                    <small className="text-muted">Created: {formatDate(record.created_at)}</small>
                </div>
            ),
        },
        {
            title: "Date",
            dataIndex: "quotation_date",
            sorter: true,
            render: (text, record) => (
                <div>
                    <div>{formatDate(text)}</div>
                    {record.expiry_date && (
                        <small className={isExpired(record.expiry_date) ? 'text-danger' : 'text-muted'}>
                            Expires: {formatDate(record.expiry_date)}
                        </small>
                    )}
                </div>
            ),
        },
        {
            title: "Customer",
            dataIndex: "customer_name",
            sorter: true,
            render: (text, record) => (
                <div className="d-flex align-items-center">
                    <div className="avatar avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center text-white me-2">
                        {text?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                        <div>{text}</div>
                        <small className="text-muted">{record.customer_phone || ''}</small>
                    </div>
                </div>
            ),
        },
        {
            title: "Amount",
            dataIndex: "grand_total",
            sorter: true,
            render: (text, record) => (
                <div>
                    <strong className="text-primary">{formatCurrency(text)}</strong>
                    <br />
                    <small className="text-muted">Items: {record.items_count || 0}</small>
                </div>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            sorter: true,
            render: (text) => getStatusBadge(text),
        },
        {
            title: "Valid Till",
            dataIndex: "expiry_date",
            render: (text) => (
                <span className={isExpired(text) ? 'text-danger' : ''}>
                    {formatDate(text)}
                    {isExpired(text) && <small className="d-block text-danger">Expired</small>}
                </span>
            ),
        },
        {
            title: "Actions",
            dataIndex: "actions",
            render: (text, record) => (
                <div className="btn-group btn-group-sm" role="group">
                    <button
                        className="btn btn-outline-info"
                        onClick={() => handleView(record)}
                        title="View Details"
                    >
                        <Eye size={14} />
                    </button>
                    <button
                        className="btn btn-outline-primary"
                        onClick={() => handleEdit(record)}
                        title="Edit"
                        disabled={!canEdit || record.status === 'converted' || record.status === 'expired'}
                    >
                        <Edit size={14} />
                    </button>
                    <button
                        className="btn btn-outline-success"
                        onClick={() => handleConvertToSale(record.id)}
                        title="Convert to Sale"
                        disabled={record.status === 'converted' || record.status === 'expired'}
                    >
                        <DollarSign size={14} />
                    </button>
                    {/* PDF Download Button */}
                    <button
                        className="btn btn-outline-warning"
                        onClick={() => downloadQuotationPDF(record)}
                        title="Download PDF"
                        disabled={loading}
                    >
                        <File size={14} />
                    </button>
                    <button
                        className="btn btn-outline-danger"
                        onClick={() => handleDelete(record)}
                        title="Delete"
                        disabled={!canDelete || record.status === 'converted'}
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>Quotations</h4>
                            <h6>Manage your sales quotations</h6>
                        </div>
                    </div>

                    <ul className="table-top-head">
                        {selectedRows.length > 0 && (
                            <li>
                                <button className="btn btn-danger btn-sm" onClick={() => { }}>
                                    <Trash2 size={16} className="me-1" />
                                    Delete ({selectedRows.length})
                                </button>
                            </li>
                        )}
                        <li>
                            {canCreate && (
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => navigate('/create-quotation')}
                            >
                                <PlusCircle size={18} className="me-2" />
                                New Quotation
                            </button>
                            )}
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export PDF')}>
                                <button className="btn btn-outline-primary">
                                    <Download size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export Excel')}>
                                <button className="btn btn-outline-success">
                                    <FileText size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Print')}>
                                <button className="btn btn-outline-secondary" onClick={() => window.print()}>
                                    <Printer size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <button className="btn btn-outline-secondary" onClick={() => loadQuotations()}>
                                    <RotateCcw size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <button
                                    className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>

                {/* Summary Cards */}
                <div className="row mb-4">
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Total Quotations</h5>
                                <h3 className="mb-0 text-primary">{stats.totalQuotations}</h3>
                                <small>All time</small>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Total Amount</h5>
                                <h3 className="mb-0 text-primary">{formatCurrency(stats.totalAmount)}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Pending</h5>
                                <h3 className="mb-0 text-warning">{stats.pendingCount}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-sm-6 col-12">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title text-muted">Approved</h5>
                                <h3 className="mb-0 text-success">{stats.approvedCount}</h3>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters Card */}
                <div className="card table-list-card">
                    <div className="card-body">
                        <div className="table-top">
                            <div className="search-set">
                                <div className="search-input">
                                    <input
                                        type="text"
                                        placeholder="Search by quotation no, customer..."
                                        className="form-control form-control-sm formsearch"
                                        value={filters.search}
                                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                    />
                                    <button className="btn btn-searchset" onClick={handleSearch}>
                                        <Search size={16} />
                                    </button>
                                </div>
                            </div>

                            <div className="search-path">
                                <div className="d-flex align-items-center">
                                    <button
                                        className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`}
                                        onClick={toggleFilterVisibility}
                                    >
                                        <Filter size={18} />
                                    </button>
                                </div>
                            </div>

                            <div className="form-sort">
                                <Sliders size={16} className="info-img me-2" />
                                <Select
                                    className="select"
                                    options={sortOptions}
                                    value={sortOptions.find(opt => opt.value === sortOption)}
                                    onChange={handleSortChange}
                                    placeholder="Sort by"
                                    isClearable={false}
                                />
                            </div>
                        </div>

                        {/* Filter Panel */}
                        <div
                            className={`card${isFilterVisible ? ' visible' : ''}`}
                            id="filter_inputs"
                            style={{ display: isFilterVisible ? 'block' : 'none' }}
                        >
                            <div className="card-body pb-0">
                                <div className="row">
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <User size={16} className="info-img" />
                                            <Select
                                                options={customers}
                                                value={customers.find(c => c.value === filters.customer_id)}
                                                onChange={(opt) => handleFilterChange('customer_id', opt?.value || 0)}
                                                placeholder="All Customers"
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <StopCircle size={16} className="info-img" />
                                            <Select
                                                options={statusFilterOptions}
                                                value={statusFilterOptions.find(s => s.value === filters.status)}
                                                onChange={(opt) => handleFilterChange('status', opt?.value || '')}
                                                placeholder="All Status"
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-6 col-sm-12 col-12">
                                        <div className="input-blocks">
                                            <div className="position-relative daterange-wraper">
                                                <Calendar size={16} className="info-img" />
                                                <RangePicker
                                                    value={filters.startDate && filters.endDate ? [filters.startDate, filters.endDate] : null}
                                                    onChange={handleDateRangeChange}
                                                    format="DD/MM/YYYY"
                                                    placeholder={['From Date', 'To Date']}
                                                    style={{ width: '100%' }}
                                                    ranges={datePresets}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <button className="btn btn-primary w-100" onClick={() => loadQuotations(1)}>
                                                <Search size={16} className="me-2" />
                                                Apply Filters
                                            </button>
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <button className="btn btn-secondary w-100" onClick={resetFilters}>
                                                <RotateCcw size={16} className="me-2" />
                                                Reset
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Quotations Table */}
                        <div className="table-responsive">
                            <Table
                                columns={columns}
                                dataSource={quotations}
                                loading={loading}
                                pagination={{
                                    current: pagination.page,
                                    pageSize: pagination.limit,
                                    total: pagination.total,
                                    onChange: handlePageChange,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} quotations`
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            {modalType === 'view' && selectedQuotation && (
                <ViewQuotation
                    quotation={selectedQuotation}
                    onClose={handleModalClose}
                />
            )}

            {modalType === 'edit' && selectedQuotation && (
                <EditQuotation
                    quotationId={selectedQuotation.id}
                    onSuccess={handleSuccess}
                    onClose={handleModalClose}
                    isOpen={true}
                />
            )}
        </div>
    );
};

export default QuotationList;